import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import './style.css';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useSnackbar } from 'notistack';
import { utils } from 'web3';
import { useSelector } from 'react-redux';
import { BUYING, SELLING } from './config';
import { confirmResponse, dispatchErr, finalizeOffer, getMetadata, respondToOffer } from '../../../util/metamask';
import CONFIG from '../../../config';
import Skeleton from '@mui/material/Skeleton';

const Row = ({ approved, blockNumber, buyer, amount, seller, tokenId, purchased, direction }) => {

    const labels = useMemo(() => new LocalizedStrings( LOCALIZATIONS ), []),
    locale = useSelector( state => state.app.locale ),
    [ loading, setLoading ] = useState( false ),
    [ boardName, setBoardName ] = useState( null ),
    buying = Boolean( direction === BUYING ),
    formattedAmount = `${ utils.fromWei( amount, "ether" ) } ETH`,
    selling = Boolean( direction === SELLING ),
    { enqueueSnackbar } = useSnackbar();

    /**
     * @name buy
     * @function
     * @description Proceeds with a transaction
     * @param {*} offer 
     * @returns 
     */
    const buy = async () => {
        const offers = await confirmResponse({ buyer, seller, tokenId }, blockNumber ),
        goodOffer = offers.length > 0,
        match = ( amount === offers[ 0 ]?.returnValues?.amount );

        // make sure this user is approved to buy this token
        if( !goodOffer ){
            enqueueSnackbar( labels.badOffer, { variant: 'error' });
        
        // make sure the offer amount is correct
        } else if( !match ){
            enqueueSnackbar( labels.badOfferAmount, { variant: 'error' });

        // go ahead with the purchase
        } else {
            const buy = new Promise( finalizeOffer({ buyer, seller, amount, tokenId }) );
            setLoading( true );
            buy
                .then( res => {
                    setLoading( false );
                    enqueueSnackbar(
                        labels.formatString(
                            labels.successfulPurchase,
                            tokenId,
                            <>&nbsp;<Link target="_blank" href={ CONFIG.etherscan.replace( '{hash}', res.transactionHash ) }>{ labels.etherscan }</Link></>
                        ),
                        { variant: 'success' }
                    )
                })
                .catch( err => {
                    setLoading( false );
                    dispatchErr( err, enqueueSnackbar, labels );
                });
        }
        
    }

    /**
     * @name get
     * @function
     * @description Retrieves pertinent metadata
     */
    const get = useCallback( async () => {
        const { name } = await getMetadata( tokenId );
        setBoardName( name );
    }, [ tokenId, setBoardName ])

    /**
     * @name approve
     * @function
     * @description Approves an offer
     * @param {object} offer 
     * @returns {function}
     */
    const respond = approved => async () => {
        setLoading( approved ? 'approving' : 'rejecting' );
        const response = new Promise( respondToOffer( approved, { buyer, amount, seller, tokenId }) );
        response
            .then( hash => {
                setLoading( false );
                enqueueSnackbar(
                    labels.formatString(
                        labels[ approved ? 'offerApproved' : 'offerRejected' ],
                        formattedAmount,
                        tokenId
                    ),
                    { variant: 'success' }
                )
            })
            .catch( err => {
                setLoading( false );
                dispatchErr( err, enqueueSnackbar, labels );
            })
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale );
    }, [ labels, locale ]);

    useEffect(() => {
        if( !boardName ) get();
    }, [ boardName, get ])

    return (
        <TableRow
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <TableCell component="th" scope="row">
                { selling ? buyer : null } 
                { buying ? seller : null }
            </TableCell>
            <TableCell align="right">{ tokenId }</TableCell>
            <TableCell width={ 300 }>
                { 
                    boardName || <Skeleton animation="wave" />
                }
            </TableCell>
            <TableCell align="right">{ formattedAmount }</TableCell>
            <TableCell>
                {
                    ( approved !== undefined && !purchased ) ?
                        <Chip label={ labels[ approved ? 'approved' : 'rejected' ] } color={ approved ? 'primary' : 'error' } /> :
                        null
                }
                
                {
                    ( purchased ) ?
                        <Chip label={ labels[ selling ? 'sold' : 'purchased' ] } color="success" /> :
                        null
                }
                {
                    buying && !purchased && approved === undefined ?
                        <Chip label={ labels.pending } /> :
                        null
                }
            </TableCell>
            <TableCell align="right">
                <Stack justifyContent={ selling ? "flex-end" : null } direction="row" spacing={ 1 }>
                    
                    {
                        buying && approved && !purchased ?
                            <Button aria-label={ labels.buy } size="small" disabled={ loading } onClick={ buy } variant="contained" startIcon={ loading ? <CircularProgress size="1em" /> : <ShoppingCartIcon /> }>
                                { labels.buy }
                            </Button> :
                            null
                    }
                    {
                        ( selling && !purchased && approved === undefined ) ?
                            <>
                                <Button aria-label={ labels.approve } size="small" disabled={ loading } variant="contained" onClick={ respond( true ) } startIcon={ loading === 'approving' ? <CircularProgress size="1em" /> :<ThumbUpIcon /> }>
                                    { labels.approve }
                                </Button>
                                
                                <Button aria-label={ labels.reject } size="small" disabled={ loading } variant="contained" color="error" onClick={ respond( false ) } startIcon={ loading === 'rejecting' ? <CircularProgress size="1em" /> :<ThumbDownIcon /> }>
                                    { labels.reject }
                                </Button>
                            </> :
                            null
                    }
                    
                </Stack>
            </TableCell> 
        </TableRow>
    )
               
}

Row.propTypes = {
    buyer: PropTypes.string, 
    direction: PropTypes.oneOf([ BUYING, SELLING ]),
    amount: PropTypes.string, 
    seller: PropTypes.string, 
    tokenId: PropTypes.string
}

export default Row;